import React, { useCallback, useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform, Pressable, type NativeSyntheticEvent, type NativeScrollEvent } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Brand, Neutrals, accentAlpha } from '@/constants/theme';
import { Button } from '@/components/ui/button';
/** Minimal staple shape for display and press (Staple and ListStaple both satisfy this) */
export interface StapleChipItem {
  id: string;
  item_name: string;
  category: string;
}

type ScrollState = { scrollLeft: number; contentWidth: number; layoutWidth: number };
const isWeb = Platform.OS === 'web';

export interface StaplesChipsProps {
  staples: StapleChipItem[];
  loading?: boolean;
  onStaplePress: (staple: StapleChipItem) => void | Promise<void>;
  onManagePress?: () => void;
  disabled?: boolean;
}

export function StaplesChips({
  staples,
  loading = false,
  onStaplePress,
  onManagePress,
  disabled = false,
}: StaplesChipsProps) {
  const [emptyStateCollapsed, setEmptyStateCollapsed] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const trackRef = useRef<View>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({ scrollLeft: 0, contentWidth: 0, layoutWidth: 0 });
  const [drag, setDrag] = useState<{ startX: number; startScrollLeft: number } | null>(null);

  const handleStaplePress = useCallback(
    (staple: StapleChipItem) => {
      if (disabled) return;
      onStaplePress(staple);
    },
    [disabled, onStaplePress]
  );

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    setScrollState({
      scrollLeft: contentOffset.x,
      contentWidth: contentSize.width,
      layoutWidth: layoutMeasurement.width,
    });
  }, []);

  const onContentSizeChange = useCallback(
    (w: number, _h: number) => {
      if (!isWeb) return;
      setScrollState((prev) => (prev.contentWidth !== w ? { ...prev, contentWidth: w } : prev));
    },
    []
  );

  const onScrollLayout = useCallback(
    (e: { nativeEvent: { layout: { width: number } } }) => {
      if (!isWeb) return;
      const layoutWidth = e.nativeEvent.layout.width;
      setScrollState((prev) => (prev.layoutWidth !== layoutWidth ? { ...prev, layoutWidth } : prev));
    },
    []
  );

  const handleTrackPress = useCallback(
    (e: { nativeEvent: { pageX: number } }) => {
      if (!isWeb || !trackRef.current || !scrollRef.current) return;
      const el = (trackRef.current as unknown as { getBoundingClientRect(): DOMRect }).getBoundingClientRect?.();
      if (!el) return;
      const { scrollLeft, contentWidth, layoutWidth } = scrollState;
      const maxScroll = contentWidth - layoutWidth;
      if (maxScroll <= 0) return;
      const fraction = (e.nativeEvent.pageX - el.left) / el.width;
      const x = Math.max(0, Math.min(maxScroll, fraction * maxScroll));
      scrollRef.current.scrollTo({ x, animated: false });
    },
    [scrollState]
  );

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!isWeb) return;
      setDrag({ startX: e.clientX, startScrollLeft: scrollState.scrollLeft });
    },
    [scrollState.scrollLeft]
  );

  useEffect(() => {
    if (!isWeb || !drag) return;
    const trackEl = trackRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
    const rect = trackEl?.getBoundingClientRect?.();
    if (!rect) return;

    const handleMove = (e: MouseEvent) => {
      const { contentWidth, layoutWidth } = scrollState;
      const maxScroll = contentWidth - layoutWidth;
      if (maxScroll <= 0) return;
      const trackWidth = rect.width;
      const thumbWidth = (layoutWidth / contentWidth) * trackWidth;
      const thumbTravel = trackWidth - thumbWidth;
      const ratio = thumbTravel > 0 ? maxScroll / thumbTravel : 0;
      const dx = (e.clientX - drag.startX) * ratio;
      const newX = Math.max(0, Math.min(maxScroll, drag.startScrollLeft + dx));
      scrollRef.current?.scrollTo({ x: newX, animated: false });
    };
    const handleUp = () => setDrag(null);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isWeb, drag, scrollState.contentWidth, scrollState.layoutWidth]);

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={Brand.accent} />
          <Text style={styles.loadingText}>Loading staples…</Text>
        </View>
      </View>
    );
  }

  if (staples.length === 0) {
    if (emptyStateCollapsed) {
      return (
        <View style={styles.container}>
          <View style={styles.emptyStateCollapsed}>
            <TouchableOpacity
              onPress={() => setEmptyStateCollapsed(false)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.emptyStateCollapsedExpand}
              accessibilityLabel="Expand staples section"
            >
              <Text style={styles.emptyStateCollapsedLabel}>Quick-add staples</Text>
            </TouchableOpacity>
            {onManagePress && (
              <TouchableOpacity
                onPress={onManagePress}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={styles.manageButton}
              >
                <Text style={styles.manageButtonText}>Manage staples</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      );
    }
    return (
      <View style={styles.container}>
        <View style={styles.emptyState}>
          <View style={styles.emptyStateTopRow}>
            <View style={styles.emptyStateTopRowSpacer} />
            <TouchableOpacity
              onPress={() => setEmptyStateCollapsed(true)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.emptyStateCloseButton}
              accessibilityLabel="Minimize staples section"
            >
              <Ionicons name="close" size={22} color={Neutrals.textSecondary} />
            </TouchableOpacity>
          </View>
          <Text style={styles.emptyStateTitle}>Quick-add staples</Text>
          <View style={styles.emptyStateIconWrap}>
            <Ionicons name="list" size={28} color={Brand.accent} />
          </View>
          <Text style={styles.emptyStateDescription}>
            Add items you buy often—milk, eggs, bread—and add them to any list in one tap.
          </Text>
          {onManagePress && (
            <Button onPress={onManagePress} style={styles.emptyStateButton}>
              Set up staples
            </Button>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.sectionLabel}>Quick add</Text>
        {onManagePress && (
          <TouchableOpacity onPress={onManagePress} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} style={styles.manageButton}>
            <Text style={styles.manageButtonText}>Manage</Text>
            <Ionicons name="chevron-forward" size={14} color={Brand.accent} />
          </TouchableOpacity>
        )}
      </View>
      <View style={isWeb ? styles.scrollBarWrapper : undefined}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          onScroll={isWeb ? onScroll : undefined}
          scrollEventThrottle={isWeb ? 16 : undefined}
          onContentSizeChange={isWeb ? onContentSizeChange : undefined}
          onLayout={isWeb ? onScrollLayout : undefined}
          contentContainerStyle={styles.chipsContent}
          style={styles.chipsScroll}
        >
          {staples.map((staple) => (
            <TouchableOpacity
              key={staple.id}
              onPress={() => handleStaplePress(staple)}
              style={[styles.chip, disabled && styles.chipDisabled]}
              activeOpacity={0.7}
            >
              <Text style={[styles.chipText, disabled && styles.chipTextDisabled]} numberOfLines={1}>
                {staple.item_name}
              </Text>
              <Ionicons name="add-circle-outline" size={16} color={disabled ? Neutrals.textMuted : Brand.accent} />
            </TouchableOpacity>
          ))}
        </ScrollView>
        {isWeb && scrollState.contentWidth > scrollState.layoutWidth && scrollState.layoutWidth > 0 && (
          <Pressable ref={trackRef as React.RefObject<View>} onPress={handleTrackPress} style={styles.scrollBarTrack}>
            <View
              style={[
                styles.scrollBarThumb,
                {
                  width: Math.max(24, (scrollState.layoutWidth / scrollState.contentWidth) * scrollState.layoutWidth),
                  left:
                    scrollState.contentWidth <= scrollState.layoutWidth
                      ? 0
                      : (scrollState.scrollLeft / (scrollState.contentWidth - scrollState.layoutWidth)) *
                        (scrollState.layoutWidth -
                          Math.max(24, (scrollState.layoutWidth / scrollState.contentWidth) * scrollState.layoutWidth)),
                },
              ]}
              {...(isWeb ? { onMouseDown: handleThumbMouseDown } : {})}
            />
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  emptyStateCollapsed: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  emptyStateCollapsedExpand: {
    padding: 4,
  },
  emptyStateCollapsedLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textSecondary,
  },
  emptyState: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 16,
    paddingVertical: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: accentAlpha(0.25),
    borderStyle: 'dashed',
  },
  emptyStateTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
    marginBottom: 4,
  },
  emptyStateTopRowSpacer: {
    flex: 1,
  },
  emptyStateCloseButton: {
    padding: 4,
  },
  emptyStateIconWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: accentAlpha(0.2),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Neutrals.textPrimary,
    marginBottom: 6,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 16,
    paddingHorizontal: 8,
  },
  emptyStateButton: {
    minWidth: 160,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  manageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  manageButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: Brand.accent,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  scrollBarWrapper: {},
  scrollBarTrack: {
    height: 10,
    marginTop: 4,
    backgroundColor: Neutrals.border,
    borderRadius: 5,
    justifyContent: 'center',
    position: 'relative' as const,
  },
  scrollBarThumb: {
    position: 'absolute' as const,
    height: 6,
    top: 2,
    borderRadius: 3,
    backgroundColor: Brand.accent,
    minWidth: 24,
  },
  chipsScroll: {},
  chipsContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: Neutrals.whiteFaded90,
    borderWidth: 2,
    borderColor: accentAlpha(0.3),
  },
  chipDisabled: {
    opacity: 0.6,
  },
  chipText: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    maxWidth: 120,
  },
  chipTextDisabled: {
    color: Neutrals.textMuted,
  },
});
